import { useMutation } from "@tanstack/react-query";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, InlineNotification, Link, Loading } from "@carbon/react";
import axios from "../../axios-orders";
import { newPasswordValidationSchema } from "../../utils";
import AuthLayout from "../carbon/AuthLayout/AuthLayout";
import CarbonForm from "../carbon/Form/Form";
import TextField from "../carbon/Form/TextField";
import "./newPassword.scss";

type FormValues = {
  email: string;
  password: string;
  confirmPassword?: string;
};

/**
 * The set-new-password screen (`POST /auth/new-password`, token read from the URL query),
 * rebuilt on the Carbon primitives (Task 6). Task 2 made the response opaque (204 No Content
 * instead of the raw `User`) — this screen already ignored the response body (`onSuccess: () =>
 * reset()`), so that's unaffected; only the mutation's `isSuccess`/`isError` status drives the
 * UI, same as before.
 *
 * A 400 gets its own message: the backend answers an identical empty 400 for an unknown email and
 * for a token that was already used or replaced by a later reset request (no enumeration oracle),
 * and the password rules can't cause it since Yup enforces the same ones first. Either way the
 * way out is a fresh link, so the message points to the reset-password screen.
 */
const isInvalidLink = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status ===
  400;

const NewPassword = () => {
  const { t } = useTranslation();
  const searchParams = useSearchParams()[0];
  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: yupResolver(newPasswordValidationSchema),
    // See Form.tsx's own comment on this same pattern: a Controller-bound field needs an
    // explicit "" default to match Yup's error selection against the pre-Carbon uncontrolled
    // `register()` inputs, whose native `value` already defaulted to "" — verified live: without
    // this, an empty submit showed the plain "Enter your password" required message instead of
    // the pinned password-complexity one.
    defaultValues: { email: "", password: "", confirmPassword: "" },
  });

  const {
    mutate: newPassword,
    isPending,
    isSuccess,
    isError,
    error,
  } = useMutation({
    mutationFn: async (data: FormValues) =>
      await axios.post("/auth/new-password", data),
    onSuccess: () => reset(),
  });

  const onSubmit = (data: FormValues) => {
    const token = searchParams.get("token");
    const newPasswordData = { ...data, token };
    newPassword(newPasswordData);
  };

  return (
    <AuthLayout>
      {!isSuccess && (
        <>
          {isPending ? (
            <Loading withOverlay={false} description={t("cta.loading")} />
          ) : (
            <>
              <h3 className="new-password__title">{t("newPassword.reset")}</h3>
              <p className="new-password__copy">{t("newPassword.insert")}</p>
              <CarbonForm
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                noValidate
                className="new-password__form"
                autoComplete="off"
              >
                <TextField
                  name="email"
                  type="email"
                  label={t("login.email")}
                  control={control}
                  required
                />

                <TextField
                  name="password"
                  type="password"
                  label={t("login.password")}
                  control={control}
                  required
                />

                <TextField
                  name="confirmPassword"
                  type="password"
                  label={t("login.confirmPassword")}
                  control={control}
                  required
                />

                <div className="new-password__requirements">
                  <p>{t("changePassword.title")}</p>
                  <ul>
                    <li>{t("changePassword.characters")}</li>
                    <li>{t("changePassword.uppercase")}</li>
                    <li>{t("changePassword.lowercase")}</li>
                    <li>{t("changePassword.number")}</li>
                    <li>{t("changePassword.special")}</li>
                  </ul>
                </div>

                <Button type="submit">{t("cta.send")}</Button>

                <div className="new-password__signin">
                  <Link as={RouterLink} to="/">
                    {t("login.signIn")}
                  </Link>
                </div>
              </CarbonForm>
            </>
          )}
        </>
      )}

      <div className="new-password__status">
        {isSuccess ? (
          <>
            <InlineNotification
              kind="success"
              lowContrast
              hideCloseButton
              title={t("login.change")}
              className="new-password__notification"
            />
            <Link as={RouterLink} to="/" className="new-password__signin-link">
              {t("login.signIn")}
            </Link>
          </>
        ) : null}

        {isError && isInvalidLink(error) ? (
          <>
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title={t("newPassword.invalidLink")}
              subtitle={t("newPassword.invalidLinkHelp")}
              className="new-password__notification"
            />
            <Link
              as={RouterLink}
              to="/reset-password"
              className="new-password__signin-link"
            >
              {t("newPassword.requestNewLink")}
            </Link>
          </>
        ) : null}

        {isError && !isInvalidLink(error) ? (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title={t("newMaterialForm.error")}
            className="new-password__notification"
          />
        ) : null}
      </div>
    </AuthLayout>
  );
};

export default NewPassword;
